/**
 * Mouse-driven single-click placement controller for MapLibre GL.
 *
 * Sister to BboxDrawer — same lifecycle (begin/end), same convention of
 * disabling map drag while active. The next click on the map fires the
 * onCommit callback with the click's lat/lon and immediately calls end()
 * — so the operator clicks the "Place" button again to drop another
 * marker. Single-shot mode keeps the UI cycle obvious (each click =
 * one explicit place action) and avoids accidentally placing markers
 * when the operator forgets the mode is on.
 */
import type { Map as MLMap, MapMouseEvent } from "maplibre-gl";

export class PlaceMarkerController {
  private map: MLMap;
  private onCommit: (lat: number, lon: number) => void;
  private onCancel: () => void;
  private originalCursor = "";
  private active = false;

  constructor(
    map: MLMap,
    onCommit: (lat: number, lon: number) => void,
    onCancel: () => void,
  ) {
    this.map = map;
    this.onCommit = onCommit;
    this.onCancel = onCancel;
  }

  begin(): void {
    if (this.active) return;
    this.active = true;
    this.originalCursor = this.map.getCanvas().style.cursor;
    this.map.getCanvas().style.cursor = "crosshair";
    this.map.dragPan.disable();
    this.map.on("click", this.handleClick);
    // ESC cancels without placing
    window.addEventListener("keydown", this.handleKey);
  }

  end(): void {
    if (!this.active) return;
    this.active = false;
    this.map.getCanvas().style.cursor = this.originalCursor;
    this.map.dragPan.enable();
    this.map.off("click", this.handleClick);
    window.removeEventListener("keydown", this.handleKey);
  }

  private handleClick = (e: MapMouseEvent): void => {
    const { lng, lat } = e.lngLat;
    this.end();
    this.onCommit(lat, lng);
  };

  private handleKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.end();
      this.onCancel();
    }
  };
}
